import { Calendar, DateField, DatePicker, Label } from '@heroui/react';
import {
    parseDate,
    parseDateTime,
    type DateValue,
} from '@internationalized/date';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import type React from 'react';
import { I18nProvider } from 'react-aria-components/I18nProvider';
import { getNodeTestProps } from '@/utils';

dayjs.extend(customParseFormat);

type DatePickerFieldProps = {
    error?: string;
    id: string;
    isDisabled?: boolean;
    isRequired?: boolean;
    label: string;
    granularity?: 'day' | 'minute';
    name: string;
    onChange: (value: string) => void;
    outputFormat?: 'YYYY-MM-DD' | 'DD/MM/YYYY' | 'YYYY-MM-DDTHH:mm';
    value: string;
};

const getDateValue = (
    value: string,
    outputFormat: NonNullable<DatePickerFieldProps['outputFormat']>,
): DateValue | null => {
    if (!value) {
        return null;
    }

    const parsedDate = dayjs(
        value,
        ['YYYY-MM-DDTHH:mm', 'YYYY-MM-DD', 'DD/MM/YYYY'],
        true,
    );

    if (!parsedDate.isValid()) {
        return null;
    }

    return outputFormat === 'YYYY-MM-DDTHH:mm'
        ? parseDateTime(parsedDate.format('YYYY-MM-DDTHH:mm'))
        : parseDate(parsedDate.format('YYYY-MM-DD'));
};

const formatDateValue = (
    value: DateValue | null,
    outputFormat: NonNullable<DatePickerFieldProps['outputFormat']>,
) => {
    if (!value) {
        return '';
    }

    const timeSuffix =
        'hour' in value && 'minute' in value
            ? `T${String(value.hour).padStart(2, '0')}:${String(
                  value.minute,
              ).padStart(2, '0')}`
            : '';

    return dayjs(
        `${value.year}-${String(value.month).padStart(2, '0')}-${String(
            value.day,
        ).padStart(2, '0')}${timeSuffix}`,
    ).format(outputFormat);
};

export const DatePickerField: React.FC<DatePickerFieldProps> = ({
    error,
    id,
    isDisabled,
    isRequired,
    label,
    granularity = 'day',
    name,
    onChange,
    outputFormat = 'YYYY-MM-DD',
    value,
}) => {
    const pickerValue = getDateValue(value, outputFormat);
    let segmentIndex = 0;

    return (
        <I18nProvider locale="en-GB">
            <DatePicker
                className="w-full"
                granularity={granularity}
                isDisabled={isDisabled}
                isInvalid={Boolean(error)}
                isRequired={isRequired}
                name={name}
                onChange={(nextValue) =>
                    onChange(formatDateValue(nextValue, outputFormat))
                }
                value={pickerValue}
                {...getNodeTestProps(id)}
            >
                <Label {...getNodeTestProps(`${id}-label`)}>{label}</Label>
                <DateField.Group
                    fullWidth
                    variant="secondary"
                    {...getNodeTestProps(`${id}-group`)}
                >
                    <DateField.Input {...getNodeTestProps(`${id}-input`)}>
                        {(segment) => {
                            const currentSegmentIndex = segmentIndex;
                            segmentIndex += 1;

                            return (
                                <DateField.Segment
                                    segment={segment}
                                    {...getNodeTestProps(
                                        `${id}-segment-${currentSegmentIndex}-${segment.type}`,
                                    )}
                                />
                            );
                        }}
                    </DateField.Input>
                    <DateField.Suffix {...getNodeTestProps(`${id}-suffix`)}>
                        <DatePicker.Trigger
                            {...getNodeTestProps(`${id}-trigger`)}
                        >
                            <DatePicker.TriggerIndicator
                                {...getNodeTestProps(`${id}-trigger-indicator`)}
                            />
                        </DatePicker.Trigger>
                    </DateField.Suffix>
                </DateField.Group>
                {error ? (
                    <p
                        className="text-sm text-danger"
                        {...getNodeTestProps(`${id}-error`)}
                    >
                        {error}
                    </p>
                ) : null}
                <DatePicker.Popover {...getNodeTestProps(`${id}-popover`)}>
                    <Calendar
                        aria-label={label}
                        {...getNodeTestProps(`${id}-calendar`)}
                    >
                        <Calendar.Header
                            {...getNodeTestProps(`${id}-calendar-header`)}
                        >
                            <Calendar.YearPickerTrigger
                                {...getNodeTestProps(
                                    `${id}-calendar-year-trigger`,
                                )}
                            >
                                <Calendar.YearPickerTriggerHeading
                                    {...getNodeTestProps(
                                        `${id}-calendar-year-heading`,
                                    )}
                                />
                                <Calendar.YearPickerTriggerIndicator
                                    {...getNodeTestProps(
                                        `${id}-calendar-year-indicator`,
                                    )}
                                />
                            </Calendar.YearPickerTrigger>
                            <Calendar.NavButton
                                slot="previous"
                                {...getNodeTestProps(`${id}-calendar-previous`)}
                            />
                            <Calendar.NavButton
                                slot="next"
                                {...getNodeTestProps(`${id}-calendar-next`)}
                            />
                        </Calendar.Header>
                        <Calendar.Grid
                            {...getNodeTestProps(`${id}-calendar-grid`)}
                        >
                            <Calendar.GridHeader
                                {...getNodeTestProps(
                                    `${id}-calendar-grid-header`,
                                )}
                            >
                                {(day) => (
                                    <Calendar.HeaderCell
                                        {...getNodeTestProps(
                                            `${id}-calendar-header-cell-${day.toLowerCase()}`,
                                        )}
                                    >
                                        {day}
                                    </Calendar.HeaderCell>
                                )}
                            </Calendar.GridHeader>
                            <Calendar.GridBody
                                {...getNodeTestProps(
                                    `${id}-calendar-grid-body`,
                                )}
                            >
                                {(date) => (
                                    <Calendar.Cell
                                        date={date}
                                        {...getNodeTestProps(
                                            `${id}-calendar-cell-${date}`,
                                        )}
                                    />
                                )}
                            </Calendar.GridBody>
                        </Calendar.Grid>
                        <Calendar.YearPickerGrid
                            {...getNodeTestProps(`${id}-calendar-year-grid`)}
                        >
                            <Calendar.YearPickerGridBody
                                {...getNodeTestProps(
                                    `${id}-calendar-year-grid-body`,
                                )}
                            >
                                {({ year }) => (
                                    <Calendar.YearPickerCell
                                        year={year}
                                        {...getNodeTestProps(
                                            `${id}-calendar-year-cell-${year}`,
                                        )}
                                    />
                                )}
                            </Calendar.YearPickerGridBody>
                        </Calendar.YearPickerGrid>
                    </Calendar>
                </DatePicker.Popover>
            </DatePicker>
        </I18nProvider>
    );
};
